import React, { useState } from 'react';
import mixpanel from 'mixpanel-browser';
import useSWR from 'swr';

import { Page } from '@nearst/ui';
import { useShop } from '@services/ShopProvider';
import { useCheckPermission } from '@services/auth/hooks';
import { getDeliveryCategories, updateChannel } from '@services/channels';
import ExpandableCategory from './ExpandableCategory';
import { compareSchedules } from './utils';
import styles from './DeliveryMenuSettings.module.scss';

const DeliveryMenuSettings = ({ channel, mutateChannel }) => {
	const { shop } = useShop();
	const { data: deliveryCategories } = useSWR(['delivery-categories', channel.id], () => getDeliveryCategories(shop.id));
	const authorized = useCheckPermission('org:channels:manage');
	const [loading, setLoading] = useState(false);

	const updateCategory = async (target, field, value, defaultChannelValue, categoryTitle) => {
		if (target.readOnly || target.disabled) return;
		setLoading(true);

		target.ariaBusy = true; // mark as loading
		try {
			let categoryOverrides = {
				...channel.config?.categoryOverrides
			};

			if (value === defaultChannelValue || (field === 'schedule' && compareSchedules(value, defaultChannelValue))) {
				// Remove the field if it matches the channel default
				if (categoryOverrides[categoryTitle]) {
					delete categoryOverrides[categoryTitle][field];
					// Remove the category entirely if it has no overrides left
					if (Object.keys(categoryOverrides[categoryTitle]).length === 0) {
						delete categoryOverrides[categoryTitle];
					}
				}
			} else {
				categoryOverrides = {
					...categoryOverrides,
					[categoryTitle]: {
						...categoryOverrides?.[categoryTitle],
						[field]: value
					}
				};
			}

			await updateChannel(shop.id, channel.type, { categoryOverrides });

			mixpanel.track('Delivery category settings updated', {
				Channel: channel.type,
				Category: categoryTitle,
				Field: field,
				Value: value
			});
		} catch (error) {
			console.error(error);
		} finally {
			await mutateChannel();
			target.ariaBusy = false;
			setLoading(false);
		}
	};

	if (!deliveryCategories) return null;

	return (
		<>
			<Page.Section>
				<h3>Menu settings</h3>
				<p className={styles.description}>Edit properties for each menu category.</p>
				{deliveryCategories.map((category) => (
					<ExpandableCategory
						key={category.title}
						category={category}
						channel={channel}
						authorized={authorized}
						loading={loading}
						updateCategory={updateCategory}
					/>
				))}
			</Page.Section>
		</>
	);
};

export default DeliveryMenuSettings;
